/**
 * useSupabaseAuth — keeps the Supabase client authenticated as the
 * currently-signed-in Clerk user.
 *
 * ADR-016 Sprint C1. Flow:
 * 1. On mount (and whenever Clerk auth state changes), calls the backend
 *    bridge `POST /api/v1/auth/supabase-token` (authenticated via Clerk
 *    JWT).
 * 2. Installs the returned Supabase JWT on the singleton client so
 *    realtime + PostgREST can apply RLS.
 * 3. Re-mints every ~50 minutes (tokens expire at 1h). The refresh
 *    interval is intentionally shorter than the TTL so a re-mint never
 *    races expiry.
 * 4. On sign-out, clears the session.
 *
 * If the bridge returns 401/503 (e.g., user not signed in yet, or
 * SUPABASE_JWT_SECRET missing on backend) we log once and skip —
 * realtime subscriptions simply stay unauthenticated (anon), which
 * sees zero rows under RLS. That's the safe default; the UI still
 * works via polling + the REST API.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../components/auth/AuthWrapper';
import { setSupabaseSession, clearSupabaseSession } from '../utils/supabaseClient';

const REFRESH_INTERVAL_MS = 50 * 60 * 1000;
const API_URL = import.meta.env.REACT_APP_API_URL || '';

export function useSupabaseAuth() {
  // Use AuthWrapper's useAuth (not Clerk's directly) so this hook works in
  // both dev mode (DevAuthProvider, no Clerk) and prod mode (ClerkProvider).
  // In dev mode, getAuthToken() returns the fake dev token — the backend
  // bridge will 401 it, which we handle gracefully below.
  const { isAuthenticated, getAuthToken } = useAuth();
  const refreshTimerRef = useRef(null);
  const hasWarnedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function mintAndInstall() {
      if (!isAuthenticated) {
        clearSupabaseSession();
        return;
      }

      try {
        const clerkJwt = await getAuthToken();
        if (!clerkJwt) return;

        const resp = await fetch(`${API_URL}/api/v1/auth/supabase-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clerkJwt}`,
          },
          body: '{}',
        });

        if (!resp.ok) {
          if (!hasWarnedRef.current) {
            hasWarnedRef.current = true;
            // eslint-disable-next-line no-console
            console.warn(
              `[useSupabaseAuth] bridge returned ${resp.status} — realtime will be anon (zero rows under RLS). ` +
              'Check backend SUPABASE_JWT_SECRET + require_auth middleware.'
            );
          }
          return;
        }

        const data = await resp.json();
        if (cancelled) return;
        if (data?.access_token) {
          setSupabaseSession(data.access_token);
        }
      } catch (err) {
        if (!hasWarnedRef.current) {
          hasWarnedRef.current = true;
          // eslint-disable-next-line no-console
          console.warn('[useSupabaseAuth] token mint failed:', err);
        }
      }
    }

    // Mint immediately, then on an interval slightly shorter than TTL.
    mintAndInstall();
    refreshTimerRef.current = setInterval(mintAndInstall, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isAuthenticated, getAuthToken]);
}
